
import { ActionAPI, ButtonAPI, DeckAPI, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, GlycemicDetailsAliases } from '../../../../codegen';
import { Endpoint } from '../../../../constants/endpoints';
import BaseController from '../../../BaseController';
import TimelineContext from '../../services/timeline-service';
import { Inject } from '@odinjs/odin';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ApplicationRoute } from '../../../../constants/routes';
import { MedicationAndDateHeaderParams } from '../../../commons/header/medication-and-date-header-params';
import { GlycemicService } from './glycemic-service';
import MedicationService from '../../services/medication-service';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../utils/parameters';
import { GlycemicControl, GlycemicProcess } from './glycemic-types';
import moment from 'moment';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import GlycemicUtils, { GlycemicAction } from './glycemic-utils';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services';

interface AbortErrorInterface {
  errors: string[]
}

@Controller({ component: GlycemicDetailsAliases.GLYCEMIC_DETAILS_LAYOUT, fragment: FragmentNames.GLYCEMIC_DETAILS })
export default class GlycemicDetailsController extends BaseController {

  @Inject(GlycemicDetailsAliases.SUSPEND_GLYCEMIC_ACTION)
  private suspendActionApi!: ActionAPI;

  @Inject(GlycemicDetailsAliases.RESCHEDULE_GLYCEMIC_ACTION)
  private rescheduleActionApi!: ActionAPI;

  @Inject(GlycemicDetailsAliases.DETAILS_DECK)
  private deckApi!: DeckAPI<GlycemicControl>;

  @Inject(GlycemicDetailsAliases.PERFORM_ACTION)
  private processActionApi!: ActionAPI;

  @Inject(GlycemicDetailsAliases.ADMINISTRATION_BUTTON)
  private performButtonApi!: ButtonAPI;

  @Inject(GlycemicDetailsAliases.ACTION_PANEL)
  private performPanelApi!: PanelAPI;

  @OnMount(GlycemicDetailsAliases.DETAILS_DECK)
  onMountDeckDetails(): void {
    this.saveContext();
    this.fetchData()
      .then((data) => {
        if (!data) {
          return;
        }

        this.buildState(data);
        this.buildContextualBar();
      });

    this.fetchProcessData()
      .then(data => {
        this.context.set({
          hasProcess: data && GlycemicUtils.verifyIfHasProcess(data),
          hasProcessGenerated: data && GlycemicUtils.verifyIfHasProcessGenerated(data),
          processes: data ? GlycemicUtils.filterProcesses(data) : [],
        });

        this.configurePerformButton(this.performButtonApi);
        this.configureActionButton(this.processActionApi);
      });
  }

  @OnClick(GlycemicDetailsAliases.SUSPEND_GLYCEMIC_ACTION)
  onClickSuspendAction(): void {
    this.redirect(ApplicationRoute.ADEP_GLYCEMIC_SUSPEND);
  }

  @OnClick(GlycemicDetailsAliases.RESCHEDULE_GLYCEMIC_ACTION)
  onClickRescheduleAction(): void {
    this.redirect(ApplicationRoute.ADEP_GLYCEMIC_RESCHEDULE)
  }

  @OnClick(GlycemicDetailsAliases.ADMINISTRATION_BUTTON)
  onClickAdministrationButton(): void {
    this.openModalToAdministration(GlycemicUtils.getPrimaryAction(this.context));
  }

  @OnClick(GlycemicDetailsAliases.PERFORM_ACTION)
  handleClickPerformByProcess(): void {
    const glycemicPerformType = GlycemicUtils.getPrimaryAction(this.context)
    if (glycemicPerformType === GlycemicAction.BY_SCHEDULE) {
      this.openModalToAdministration(GlycemicAction.BY_PROCESS);
      return;
    }

    this.openModalToAdministration(GlycemicAction.BY_SCHEDULE);
  }

  @OnMount(GlycemicDetailsAliases.ADMINISTRATION_BUTTON)
  handleAdministrationButtonMount(event: MountEvent): void {
    const buttonApi = event.target as ButtonAPI;
    this.configurePerformButton(buttonApi);
  }

  @OnMount(GlycemicDetailsAliases.PERFORM_ACTION)
  handleActionMount(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    this.configureActionButton(actionApi);
  }

  private configureActionButton(actionApi: ActionAPI): void {
    const glycemicAction = GlycemicUtils.getPrimaryAction(this.context);
    const param3Value = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    const hasProcessGenerated = this.context.get('hasProcessGenerated');

    actionApi.setEnabled(false);

    if (!this.isAllowedToPerform() && !this.isAllowedToPerformParameter()) {
      return;
    }

    if (glycemicAction === GlycemicAction.BY_PROCESS) {
      actionApi.setEnabled(true);
      actionApi.setText(GlycemicAction.BY_SCHEDULE);
      return;
    }

    if (glycemicAction === GlycemicAction.BY_SCHEDULE && param3Value === 'N' && hasProcessGenerated) {
      actionApi.setEnabled(true);
      actionApi.setText(GlycemicAction.BY_PROCESS);
    }

  }

  private configurePerformButton(buttonApi: ButtonAPI): void  {
    const primaryAction = GlycemicUtils.getPrimaryAction(this.context);
    buttonApi.setValue(this.isConventional() ? primaryAction : 'text.realize');
    if (buttonApi.isVisible()) {buttonApi.setVisible(false)}

    if (this.isAllowedToPerform() && this.isAllowedToPerformParameter()) {
      buttonApi.setVisible(true);
      buttonApi.setEnabled(true);
      this.processActionApi.setEnabled(true);
    } else {
      this.performPanelApi.setVisible(false);
    }
  }

  private isAllowedToPerform(): boolean {
    const { status } = TimelineContext.get();

    return ['PENDING', 'ADDITIONAL_ATTACK_DOSE'].includes(status);
  }

  private isAllowedToPerformParameter(): boolean {
    const param13 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOWS_TO_PERFORM_PRESCRIPTION_ITEMS);
    return param13 === 'S';
  }

  private saveContext(): void {
    const { scheduleSequenceNumber, encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const { itemType } = TimelineContext.get();

    this.context.set({ encounterId, itemType, patientId, scheduleSequenceNumber });
  }

  private buildContextualBar(): void {
    this.suspendActionApi.setEnabled(this.isConventional() && this.isAllowedToSuspend());
    this.rescheduleActionApi.setEnabled(this.isConventional() && this.isAllowedToReschedule());
  }

  private isAllowedToSuspend(): boolean {
    const param509 = FoundationUtils.findModuleParam('ADEP#1113', ADEP_E1113Parameters.ALLOW_SUSPEND_MEDICATION);
    const { status } = TimelineContext.get();
    return 'S' === param509 && !['A', 'ADMINISTERED'].includes(status);
  }

  private isAllowedToReschedule(): boolean {
    const param29 = FoundationUtils.findModuleParam('ADEP#1113', ADEP_E1113Parameters.ALLOW_RESCHEDULE_MEDICATION);
    const prescriptionValidate = moment(GlycemicService.instance?.prescriptionValidate, 'DD/MM/YYYY hh:mm:ss');
    const today = moment();
    const { status } = TimelineContext.get();
    return 'S' === param29 && ['N', 'PENDING'].includes(status) && today.isBefore(prescriptionValidate);
  }

  private fetchData(): Promise<GlycemicControl | void> {
    return HttpService.get<GlycemicControl>(Endpoint.EMAR_GET_GLYCEMIC_DETAILS, this.normalizeParameters())
      .then(response => Promise.resolve(response.body))
      .catch(this.handleErrorResponse);
  }

  private fetchProcessData(): Promise<GlycemicProcess[] | void> {
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams()

    return HttpService.get<GlycemicProcess[]>(Endpoint.EMAR_GET_GLYCEMIC_PROCESSES_DATA, { scheduleSequenceNumber })
      .then(response => Promise.resolve(response.body))
      .catch(this.handleErrorResponse);
  }

  private handleErrorResponse(error: HttpResponse<AbortErrorInterface>): void {
    error.body?.errors && error.body.errors.forEach((err: string) => {
      MedicationService.showToast(err, ToastLevel.ERROR);
    });
  }

  private normalizeParameters(): Record<string, Value> {
    const { itemType, scheduleSequenceNumber } = this.context.getBulkData() as Record<string, Value>;
    return {
      scheduleSequenceNumber,
      type: itemType,
    }
  }

  private buildState(data: GlycemicControl): void {
    MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
      medicationName: data.itemDescription,
      scheduleDate: data.scheduleDate,
      type: 'GLYCEMIC'
    }

    GlycemicService.instance = data;

    this.deckApi.setData([data]);
  }

  private isConventional(): boolean {
    const itemType = this.context.get('itemType');
    return itemType === 'G';
  }

  private redirect(route: string): void {
    const { patientId, encounterId } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(route, { encounterId, patientId });
  }

  private openModalToAdministration(performType: GlycemicAction): void {
    const { processes } = this.context.getBulkData() as Record<string, Value>;

    ModalService.openDefault({
      contextData: {
        ...GlycemicService.instance,
        isConventional: this.isConventional(),
        onFormSubmit: (applicationSite: Record<string, unknown>) => this.context.set({ applicationSite }),
        performType,
        processes,
      },
      fragment: FragmentNames.GLYCEMIC_ADMINISTRATION,
      id: FragmentNames.GLYCEMIC_ADMINISTRATION,
      title: this.isConventional() ? performType : 'text.realize',
    });
  }
}
